import { formatDate, formatDateWithTime } from "helpers/date";
import { TransportSedel } from "types/transportSedel.type";
import { Uppdrag } from "types/uppdrag.type";
import { TransportSedelModal } from "./TransportSedelModal";
import { useDeleteTransportSedelLogic } from "./useDeleteTransportSedelLogic";
import DangerModal from "components/DangerModal";
import { SignatureSection } from "./SignatureSection";
import { useTransportSedelStatusAnimation } from "./useTransportSedelStatusAnimation";
import "./pulseAnimation.css";

import { TransportSedelStatusTag } from "components/TransportSedelStatusTag";
import { DotMenu } from "components/DotMenu";
import { useUpdateTransportSedelLogic } from "./useUpdateTransportSedelLogic";
import { Headline4 } from "components/typography/Headline4";
import {
  Item,
  Label,
  MultiColumnList,
  Value,
} from "components/multiColumnList/MultiColumnList";
import { Link } from "components/Link";
import { isPdf } from "helpers/file";
import {
  FilePreviewSmall,
  InspectionPDFPreviewSmall,
} from "components/FilePreviewSmall";
import { usePreviewPdf, getPdfProxyUrl } from "hooks/usePreviewPdf";
import { WasteStationNotificationStatusBadge } from "components/WasteStationNotificationStatusBadge";
import { useIsFeatureFlagEnabled } from "hooks/useFeatureFlag";
import { FeatureFlagName } from "api/featureFlag.api";

type Props = {
  transportSedel: TransportSedel;
  uppdrag: Uppdrag;
};
export const TransportSedelItemDetails = ({
  transportSedel,
  uppdrag,
}: Props) => {
  const updateTransportSedelLogic = useUpdateTransportSedelLogic(
    uppdrag,
    transportSedel,
  );
  const deleteTransportSedelLogic =
    useDeleteTransportSedelLogic(transportSedel);

  // Use the animation hook that detects status changes and manages animation state
  const { isPulsing } = useTransportSedelStatusAnimation(transportSedel);

  const { previewPdf, isLoading, currentUrl } = usePreviewPdf();

  const isWasteStationNotificationEnabled = useIsFeatureFlagEnabled(
    FeatureFlagName.IsWasteStationNotificationEnabled,
  );

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <Headline4>Detaljer</Headline4>
        <DotMenu
          items={[
            {
              label: "Redigera",
              onClick: updateTransportSedelLogic.openModal,
            },
            {
              label: "Ta bort",
              onClick: deleteTransportSedelLogic.openModal,
              isDelete: true,
            },
          ]}
        />

        <TransportSedelModal
          title={"Redigera transportsedel"}
          logic={updateTransportSedelLogic}
        />
        <DangerModal
          isOpen={deleteTransportSedelLogic.isModalOpen}
          onClose={deleteTransportSedelLogic.onModalClose}
          actionHandler={deleteTransportSedelLogic.onConfirm}
          isLoading={deleteTransportSedelLogic.mutation.isPending}
          title="Är du säker på att du vill ta bort transportsedeln?"
          paragraphs={["Detta går inte att ångra."]}
          actionText="Ta bort"
        />
      </div>
      {/* <MoveToButtonAnimation /> */}
      <MultiColumnList className="md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3">
        <Item>
          <Label>Status</Label>
          <Value>
            <div className="inline-flex">
              <div className={isPulsing ? "pulse-animation" : ""}>
                <TransportSedelStatusTag status={transportSedel.status} />
              </div>
            </div>
          </Value>
        </Item>
        <Item>
          <Label>Mottagarstation</Label>
          <Value>
            {transportSedel.wasteStation ? (
              <Link
                to="/app/mottagsstation/$id"
                params={{ id: transportSedel.wasteStation.id.toString() }}
                variant="gray"
              >
                {transportSedel.wasteStation.name}
              </Link>
            ) : (
              <></>
            )}
          </Value>
        </Item>
        <Item>
          <Label>Tidpunkt för avfärd</Label>
          <Value>{formatDateWithTime(transportSedel.timeOfDepartureUtc)}</Value>
        </Item>
        <Item>
          <Label>Fordon</Label>
          <Value>{transportSedel.vehicle?.registrationNumber}</Value>
        </Item>

        <SignatureSection transportSedel={transportSedel} uppdrag={uppdrag} />
        {isWasteStationNotificationEnabled &&
          !!uppdrag.wasteStationNotification && (
            <Item>
              <Label>Förfrågan till mottagarstation</Label>
              <Value>
                <div className="flex flex-col gap-y-3">
                  <div className="flex items-center gap-x-1">
                    <span>
                      {`Vi önskar lämna ${formatDate(
                        uppdrag.wasteStationNotification.requestedDate,
                      )}`}
                    </span>
                    <WasteStationNotificationStatusBadge
                      notification={uppdrag.wasteStationNotification}
                    />
                  </div>
                  {!!uppdrag.wasteStationNotification
                    .messageFromWasteStation && (
                    <div className="max-w-sm self-start whitespace-pre-wrap rounded-2xl rounded-tl-sm border border-gray-75 bg-gray-25 px-3.5 py-2.5 text-gray-800">
                      {uppdrag.wasteStationNotification.messageFromWasteStation}
                    </div>
                  )}
                  {uppdrag.wasteStationNotification.fileList.length > 0 && (
                    <div className="flex flex-col gap-y-2">
                      {uppdrag.wasteStationNotification.fileList.map((f) =>
                        isPdf(f.name) ? (
                          <InspectionPDFPreviewSmall
                            key={f.id}
                            inspectionName={f.nameWithoutExtension as string}
                            isLoading={
                              isLoading && currentUrl === getPdfProxyUrl(f.url)
                            }
                            onClick={() => previewPdf(getPdfProxyUrl(f.url))}
                          />
                        ) : (
                          <FilePreviewSmall
                            key={f.id}
                            url={f.url}
                            name={`${f.name}`}
                          />
                        ),
                      )}
                    </div>
                  )}
                </div>
              </Value>
            </Item>
          )}
      </MultiColumnList>
    </>
  );
};
